<template>
	<div class="cars-filters">
		<Transition name="fade" mode="out-in">
			<div v-if="showCurrentFilters" class="cars-filters__selected">
				<CarsFiltersSingleFilterSelected isDeleteAll :filtersData="filtersData" />
				<CarsFiltersSingleFilterSelected v-if="selectedGroupModels.length >= 1" :groups="selectedGroupModels" :filtersData="filtersData" />
				<CarsFiltersSingleFilterSelected v-if="activePriceRange" :priceRange="activePriceRange" :filtersData="filtersData" />
				<CarsFiltersSingleFilterSelected v-for="(filter, i) in activeFilters" :key="i" :filter="filter" :filtersData="filtersData" />
			</div>
		</Transition>
		<Transition name="fade" mode="out-in">
			<div v-if="showCurrentFilters" class="cars-filters__divider-container">
				<div class="cars-filters__divider"></div>
			</div>
		</Transition>
		<div class="cars-filters__select cars-filters__select--mobile">
			<CarsFiltersSingleFilter v-for="(filter, i) in mobileFiltersData" :key="i" :filter="filter" />
		</div>
		<div class="cars-filters__select cars-filters__select--web">
			<CarsFiltersSingleFilter v-for="(filter, i) in filtersData" :key="i" :filter="filter" />
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ISingleFilterProps } from './SingleFilter/SingleFilter.vue'
import { EFilterNames, ESortValues, type IPriceRange } from '@/modules/carsModule/types/filters'
import type { useUrlFilters } from '@/modules/carsModule/composables/useUrlFilters'

const carsStore = useCarsStore()
const urlFiltersRef = inject<ReturnType<typeof useUrlFilters>>('urlFilters')!

// Derive active state from URL for the Selected pills display
const selectedGroupModels = computed<IProductGroupModel[]>(() => {
	const groupNames = urlFiltersRef.state.value.groups
	if (!groupNames.length) return []
	return (carsStore.getAllGroups ?? []).filter((g) => groupNames.includes(g.name))
})

const activePriceRange = computed<IPriceRange | null>(() => {
	const s = urlFiltersRef.state.value
	if (s.priceMin == null && s.priceMax == null) return null
	const priceRanges: IPriceRange[] = [
		{ name: '0 zł - 100 zł', value: '0-100', min: 0, max: 10000 },
		{ name: '101 zł - 200 zł', value: '100-200', min: 10100, max: 20000 },
		{ name: '201 zł - 300 zł', value: '200-300', min: 20100, max: 30000 },
		{ name: '301 zł +', value: '300+', min: 30100 },
	]
	return priceRanges.find((r) => r.min === s.priceMin && (r.max ?? null) === s.priceMax) ?? null
})

// Build ICarSelectedFilters[] from URL state for the Selected pills
const activeFilters = computed<ICarSelectedFilters[]>(() => {
	const s = urlFiltersRef.state.value
	const filters: ICarSelectedFilters[] = []

	if (s.transmission.length > 0) {
		const param = carsStore.getFilterByParameterName(EFilterNames.TRANSMISSION)
		if (param) filters.push({ parameter: param, values: [...s.transmission] })
	}

	if (s.fuelType.length > 0) {
		const param = carsStore.getFilterByParameterName(EFilterNames.FUEL_TYPE)
		if (param) filters.push({ parameter: param, values: [...s.fuelType] })
	}

	if (s.seatsMin != null) {
		const param = carsStore.getFilterByParameterName(EFilterNames.SEATS)
		if (param) filters.push({ parameter: param, values: [], min: s.seatsMin })
	}

	return filters
})

const showCurrentFilters = computed(() => activeFilters.value.length > 0 || selectedGroupModels.value.length > 0 || activePriceRange.value != null)

const filtersData: ISingleFilterProps[] = [
	{
		title: 'vehicle_category',
		filterName: EFilterNames.GROUP,
	},
	{
		title: 'transmission',
		filterName: EFilterNames.TRANSMISSION,
		items: [
			{ name: 'automatic', value: 'automatic' },
			{ name: 'manual', value: 'manual' },
		],
	},
	{
		title: 'number_of_seats',
		isSeats: true,
		filterName: EFilterNames.SEATS,
	},
	{
		title: 'engine',
		filterName: EFilterNames.FUEL_TYPE,
	},
	{
		title: 'sort',
		filterName: EFilterNames.SORT,
		items: [
			{ name: '-popularity', value: ESortValues.MOST_POPULAR },
			{ name: 'price', value: ESortValues.PRICE_ASC },
			{ name: '-price', value: ESortValues.PRICE_DESC },
			{ name: 'segment_sort_order', value: ESortValues.DEFAULT },
		],
		isSort: true,
	},
]

const mobileFiltersData: ISingleFilterProps[] = [
	{
		title: 'filters',
		isAll: true,
	},
	{
		title: 'sort',
		isSort: true,
		items: [
			{ name: '-popularity', value: ESortValues.MOST_POPULAR },
			{ name: 'price', value: ESortValues.PRICE_ASC },
			{ name: '-price', value: ESortValues.PRICE_DESC },
			{ name: 'segment_sort_order', value: ESortValues.DEFAULT },
		],
		filterName: EFilterNames.SORT,
	},
]
</script>

<style lang="scss" scoped src="./Filters.scss"></style>
